/**
 * Create a wpm namespace under which all functions are declared
 */

// https://stackoverflow.com/a/5947280/4688612

(function (pmw, undefined) {

	const pmwDeduper = {
		keyName          : "_wpm_order_ids",
		cookieExpiresDays: 365,
	};

	const REST_AVAILABLE_KEY = "restEndpointAvailable";

	pmw.emailSelected         = false;
	pmw.paymentMethodSelected = false;

	/**
	 * Returns the cached REST availability status.
	 * - true:      REST was tested and works
	 * - false:     REST was tested and failed
	 * - undefined: not tested yet
	 */
	pmw.getRestStatus = () => pmw.retrieveData(REST_AVAILABLE_KEY);

	/**
	 * HTTP statuses that mean the REST route itself is unusable for this
	 * visitor, so every later REST call would fail the same way:
	 * - 404 / 405: the route is missing or the method is not allowed
	 *   (REST API disabled, rewrite rules broken, a proxy stripping /wp-json/)
	 * - 401 / 403: a security plugin or WAF blocks the REST API for anonymous
	 *   visitors. That block is deterministic for the visitor, so probing REST
	 *   again on every event would only double the requests.
	 *
	 * Every other non-OK status (429 from the /sse/ rate limit, 5xx, 408, 400)
	 * is treated as transient: that one request falls back to AJAX, but the
	 * session keeps trying REST.
	 *
	 * @since 1.71.1
	 */
	const REST_UNAVAILABLE_STATUSES = [401, 403, 404, 405];

	/**
	 * Whether a non-OK REST response means REST is unusable for the session.
	 *
	 * @param {number} status HTTP status code
	 * @returns {boolean}
	 *
	 * @since 1.71.1
	 */
	pmw.isRestUnavailableStatus = status => REST_UNAVAILABLE_STATUSES.includes(status);

	/**
	 * Try a request via REST first; if REST fails (or is already known to be
	 * unavailable), fall back to AJAX so the request is never lost.
	 *
	 * On the very first call in a session the REST status is unknown, so we
	 * optimistically attempt REST. The result is cached for the session:
	 *   - success: future calls go straight to REST (incl. sendBeacon)
	 *   - network error or a missing / blocked route (401, 403, 404, 405):
	 *     future calls skip REST and use AJAX directly
	 *   - any other non-OK status (429, 5xx, ...): only this request falls
	 *     back to AJAX, nothing is cached, so one transient rate limit or
	 *     server hiccup does not move the whole session to AJAX
	 *
	 * @param {Object}          restOptions            fetch() init for the REST call
	 * @param {string}          restOptions.url        Full REST URL
	 * @param {RequestInit}     restOptions.init       fetch() options (method, headers, body…)
	 * @param {Function|null}   ajaxFallback           Callback that performs the AJAX request.
	 *                                                 Receives no arguments; the caller closes
	 *                                                 over the payload. Return value is ignored.
	 *                                                 Pass null to skip AJAX fallback entirely.
	 * @returns {Promise<Response|null>}               The fetch Response on REST success, or null
	 *                                                 when AJAX fallback was used (or both failed).
	 */
	pmw.tryRestOrAjax = async (restOptions, ajaxFallback = null) => {

		// REST already known to be unavailable, go straight to AJAX
		if (pmw.getRestStatus() === false) {
			if (ajaxFallback) ajaxFallback();
			return null;
		}

		try {
			const response = await fetch(restOptions.url, restOptions.init);

			if (response.ok) {
				if (pmw.getRestStatus() !== true) {
					pmw.storeData(REST_AVAILABLE_KEY, true);
				}
				return response;
			}

			if (pmw.isRestUnavailableStatus(response.status)) {
				// The route is missing or blocked, mark REST unavailable for the session
				pmw.storeData(REST_AVAILABLE_KEY, false);
				pmw.console.error("REST request failed (HTTP " + response.status + "), using AJAX for the rest of the session");
			} else {
				// Transient (rate limit, server error): fall back for this request only
				pmw.console.error("REST request failed (HTTP " + response.status + "), falling back to AJAX for this request");
			}

		} catch (e) {
			pmw.storeData(REST_AVAILABLE_KEY, false);
			pmw.console.error("REST request error, falling back to AJAX: ", e);
		}

		// REST failed, replay via AJAX so the request is not lost
		if (ajaxFallback) ajaxFallback();
		return null;
	};

	/**
	 * This function writes the provided orderId into storage. It will first retrieve the existing orderIds
	 * from the storage if any, then add the new orderId into this existing array of orderIds  and stores
	 * the updated array back to the storage.
	 * If the function pmw.storeOrderIdOnServer exists and order_duplication_prevention is set to true in pmwDataLayer.shop,
	 * this function will also store the orderId, orderKey and source on the server side using pmw.storeOrderIdOnServer.
	 *
	 * @param {String}  orderId  - The ID of the order to be stored.
	 * @param {String}  orderKey - The Key of the order to be stored.
	 * @param {String}  source - The source from which the order originated (default: 'thankyou_page').
	 */
	pmw.writeOrderIdToStorage = (orderId, orderKey, source = "thankyou_page") => {

		// Get existing order ids
		let orderIds = pmw.retrieveData("orderIds", true) || [];

		// Add the new order id
		orderIds.push(orderId);

		// Store the order ids
		pmw.storeData("orderIds", orderIds, true);

		if (typeof pmw.storeOrderIdOnServer === "function") {
			pmw.storeOrderIdOnServer({
				orderId : orderId,
				orderKey: orderKey,
				source  : source,
			});
		}
	};

	pmw.isOrderIdStored = orderId => {

		// If order deduplication is disabled, return false
		if (!pmwDataLayer.shop.order_duplication_prevention) {
			pmw.console.log("order duplication prevention is off");
			return false;
		}

		const orderIds = pmw.retrieveData("orderIds", true) || [];
		return orderIds.includes(orderId);
	};

	/**
	 * Check if the email address is valid
	 *
	 * https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
	 * https://www.w3.org/TR/html5/forms.html#valid-e-mail-address
	 * https://fightingforalostcause.net/content/misc/2006/compare-email-regex.php
	 *
	 * @param email
	 * @returns {boolean}
	 */
	pmw.isEmail = email => {

		/**
		 * GitHub Copilot generated RFC 5322 compliant regex
		 * - Don't allow emails without a top-level domain like "john@localhost"
		 * - Don't allow emails with dots at the end of the name like "john.doe.@example.com"
		 */
		const regex = new RegExp(
			"^(([^<>()\\[\\]\\\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\"]+)*)|(\".+\"))"
			+ "@"
			+ "((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])"
			+ "|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
		);

		return regex.test(email);
	};

	/**
	 * Timestamp of the last cart change the Pixel Manager tracked itself.
	 *
	 * WooCommerce Blocks announce cart mutations without saying what changed
	 * (see blocks-fallbacks.js). This stamp is how the blocks listener tells a
	 * change it has already tracked apart from one that happened through a path
	 * it cannot see, and therefore whether it has to reconcile against the
	 * server to recover the missing events.
	 *
	 * @type {number}
	 */
	pmw.lastTrackedCartChange = 0;

	pmw.markCartChangeAsTracked = () => {
		pmw.lastTrackedCartChange = Date.now();
	};

	pmw.removeProductFromCart = async (productId, quantityToRemove = null) => {

		try {

			pmw.markCartChangeAsTracked();

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			let quantity;

			if (quantityToRemove == null) {
				quantity = pmwDataLayer.cart[productId].quantity;
			} else {
				quantity = quantityToRemove;
			}

			if (!pmwDataLayer.cart[productId]) {
				await pmw.getProductsFromBackend([productId]);
			}

			let product = pmw.getProductDetailsFormattedForEvent(productId, quantity);

			pmw.bus.emit("pmw:remove-from-cart", product);

			if (quantityToRemove == null || pmwDataLayer.cart[productId].quantity === quantityToRemove) {
				delete pmwDataLayer.cart[productId];
			} else {
				pmwDataLayer.cart[productId].quantity = pmwDataLayer.cart[productId].quantity - quantity;
			}

			pmw.storeCartData(pmwDataLayer.cart);

		} catch (e) {
			pmw.console.error("error: ", e);
		}
	};

	pmw.getIdBasedOndVariationsOutputSetting = productId => {

		try {
			if (pmwDataLayer?.shop?.variations_output) return productId;

			if (pmwDataLayer?.products?.[productId]?.is_variation) return pmwDataLayer.products[productId].parent_id;

			return productId;

		} catch (e) {
			console.error(e);
		}
	};

	// add_to_cart
	pmw.addProductToCart = async (productId, quantity) => {

		// Cast quantity to number to make sure it's a number
		quantity = Number(quantity);

		try {

			pmw.markCartChangeAsTracked();

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			productId = pmw.getIdBasedOndVariationsOutputSetting(productId);

			if (!productId) throw Error("Wasn't able to retrieve a productId");

			if (!pmwDataLayer?.products?.[productId]) {
				await pmw.getProductsFromBackend([productId]);
			}

			let product = pmw.getProductDetailsFormattedForEvent(productId, quantity);

			pmw.bus.emit("pmw:add-to-cart", product);

			// add product to cart pmwDataLayer['cart']

			// if the product already exists in the object, only add the additional quantity
			// otherwise create that product object in the pmwDataLayer['cart']
			if (pmwDataLayer?.cart[productId]) {

				pmwDataLayer.cart[productId].quantity = pmwDataLayer.cart[productId].quantity + quantity;
			} else {

				if (!("cart" in pmwDataLayer)) pmwDataLayer.cart = {};

				pmwDataLayer.cart[productId] = pmw.getProductDetailsFormattedForEvent(productId, quantity);
			}

			pmw.storeCartData(pmwDataLayer.cart);

		} catch (e) {
			console.error(e);

			// fallback if pmwDataLayer.cart and pmwDataLayer.products got out of sync in case cart caching has an issue
			pmw.getCartItemsFromBackend();
		}
	};

	pmw.initCart = () => {

		const storedCart = pmw.retrieveCartData();

		// If the WC cart doesn't exist, eg. after a confirmed purchase,
		// and we still have a cart object in the storage, reset it.
		if (!pmw.platform.cartExists()) {

			if (Object.keys(storedCart).length !== 0) {
				pmw.storeCartData({});
			}

			pmw.deleteDataKey("cart_hash");

			// The cart is gone, so re-arm the checkout-page-load begin_checkout
			// trigger. The next checkout in this session is a new one.
			pmw.deleteDataKey("begin_checkout_fired");

			return;
		}

		/**
		 * The WC cart exists server-side. Detect carts that were populated or
		 * changed outside of client-side tracking (funnel builders such as
		 * CartFlows adding products while rendering a page, persistent carts
		 * restored on login, coupon auto-adds): either the stored cart is
		 * empty even though the cart cookie says the cart has items, or the
		 * WooCommerce cart hash cookie no longer matches the hash recorded at
		 * the last sync. In both cases the server cart is the source of
		 * truth, so re-sync it from the backend.
		 *
		 * The stored hash is kept current for client-tracked changes through
		 * the added_to_cart / removed_from_cart events (see
		 * event_listeners.js), so this fetch only runs on genuine desyncs.
		 */
		const serverCartHash = pmw.platform.getCartHash();
		const storedCartHash = pmw.retrieveData("cart_hash");

		if (
			Object.keys(storedCart).length === 0
			|| (serverCartHash && serverCartHash !== storedCartHash)
		) {
			/**
			 * Seed the data layer with the stored cart before the refetch.
			 *
			 * getCartItemsFromBackend is asynchronous, and leaving the cart
			 * empty until it answers is not free: a shopper can click the mini
			 * cart's checkout button at any moment, and begin_checkout is a
			 * navigation event, so it dispatches synchronously on that click
			 * with no chance to wait. That window is what sent hollow
			 * begin_checkout events from product and category pages, the
			 * checkout-page-load path being the only one that waits for the
			 * cart (see triggerBeginCheckoutWhenCartReady).
			 *
			 * On a hash mismatch the stored cart is stale by definition, but a
			 * stale cart describes the checkout far better than no cart at
			 * all, and the fetch overwrites it moments later. When the stored
			 * cart is empty there is nothing to seed and nothing changes.
			 */
			if (Object.keys(storedCart).length > 0) {
				pmw.saveCartObjectToDataLayer(storedCart);
			}

			/**
			 * The stored cart is deliberately left alone here. The fetch
			 * overwrites it once it succeeds. Clearing it up front meant a
			 * failed fetch (blocked endpoint, WAF, offline) left the next page
			 * load with no cart at all instead of the stale one, and with no
			 * cart to seed the data layer from. The cart hash is only stored on
			 * success, so the next page load still retries the sync.
			 */
			pmw.getCartItemsFromBackend();
			return;
		}

		pmw.saveCartObjectToDataLayer(storedCart);
	};

	/**
	 * Whether a begin_checkout event has already fired for the current cart.
	 *
	 * Every begin_checkout stamps this flag (see the pmw:begin-checkout listener
	 * in event_listeners.js), and pmw.initCart resets it when the WooCommerce
	 * cart ceases to exist, so the flag lives exactly as long as the cart it
	 * belongs to (within the session).
	 */
	pmw.hasBeginCheckoutFired = () => true === pmw.retrieveData("begin_checkout_fired");

	/**
	 * Trigger the begin_checkout event once the cart has been synced.
	 *
	 * pmw.initCart may fetch the cart from the backend asynchronously (e.g. on
	 * funnel pages where the cart was populated server-side), so wait for
	 * pmwDataLayer.cart to be populated before firing, to make sure the event
	 * carries the products, value, and currency. After ~5 seconds, fire anyway:
	 * the WooCommerce cart cookie says the cart has items, and an event without
	 * commerce fields is better than a missing funnel step.
	 */
	pmw.triggerBeginCheckoutWhenCartReady = (attempt = 0) => {

		// A begin_checkout may have fired while waiting, e.g. through a
		// custom snippet. Never duplicate it.
		if (pmw.hasBeginCheckoutFired()) return;

		if (pmwDataLayer?.cart && Object.keys(pmwDataLayer.cart).length > 0) {
			pmw.bus.emit("pmw:begin-checkout");
			return;
		}

		// The backend answered and the cart is genuinely empty (the items
		// cookie was stale): nothing is being checked out, don't fire.
		if (pmw.cartSyncedFromBackend) return;

		if (attempt >= 20) {
			pmw.bus.emit("pmw:begin-checkout");
			return;
		}

		setTimeout(() => pmw.triggerBeginCheckoutWhenCartReady(attempt + 1), 250);
	};

	// get all cart items from the backend
	pmw.getCartItemsFromBackend = () => {
		try {

			/**
			 * Can't use a REST API endpoint, as the cart session will not be loaded if the
			 * endpoint is called.
			 *
			 * https://wordpress.org/support/topic/wc-cart-is-null-in-custom-rest-api/#post-11442843
			 */

			/**
			 * Get the cart items from the backend the data object using fetch API
			 * and log success or error messages
			 * and url encoded data
			 */
			fetch(pmw.ajax_url, {
				method   : "POST",
				cache    : "no-cache",
				body     : new URLSearchParams({action: "pmw_get_cart_items"}),
				keepalive: true,
				redirect : "error",
			})
				.then(response => {
					if (response.ok) {
						return response.json();
					} else {
						throw Error("Error getting cart items from backend");
					}
				})
				.then(data => {

					if (data.success) {

						if (!data.data["cart"]) data.data["cart"] = {};
						pmw.saveCartObjectToDataLayer(data.data["cart"]);
						pmw.storeCartData(data.data["cart"]);

						// The data layer cart now reflects the server cart. Lets
						// waiters (pmw.triggerBeginCheckoutWhenCartReady) tell a
						// genuinely empty cart apart from a pending sync.
						pmw.cartSyncedFromBackend = true;

						// Record the WooCommerce cart hash this sync corresponds to,
						// so initCart only re-fetches when the cart changes outside
						// of client-side tracking.
						pmw.storeData("cart_hash", pmw.platform.getCartHash());

						// Save cart_item_keys from AJAX response for remove_from_cart tracking
						// This provides fallback when inline scripts are suppressed via pmw_output_cart_item_data filter
						if (data.data["cart_item_keys"]) {
							pmwDataLayer.cart_item_keys = Object.assign(
								pmwDataLayer.cart_item_keys || {},
								data.data["cart_item_keys"]
							);
						}

					} else {
						throw Error("Error getting cart items from backend");
					}
				})
				.catch(error => {
					// The surrounding try/catch cannot catch async rejections. Without
					// this handler a blocked endpoint would fail silently. The cart
					// hash is deliberately not stored on failure, so the next page
					// load retries the sync once the endpoint is reachable again.
					pmw.console.error("Reading the cart for tracking failed. This request only reads the cart, it never changes it.", error);
				});

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Reconcile the data layer cart against the server cart and emit the
	 * events for whatever changed in between.
	 *
	 * getCartItemsFromBackend overwrites the cart silently, which is right when
	 * the cart was populated outside the browser (a funnel builder adding a
	 * product while the page renders, a persistent cart restored on login).
	 * This one is for the opposite case: the shopper did change the cart in the
	 * browser, but through a path that reports no product, which is what the
	 * Interactivity API add-to-cart paths do. Applying the differences through
	 * the normal add/remove functions emits the events that were missed and
	 * leaves the data layer consistent.
	 *
	 * @since 1.65.0
	 *
	 * @returns {Promise<void>}
	 */
	pmw.reconcileCartFromBackend = async () => {

		try {

			/**
			 * Same endpoint as getCartItemsFromBackend: the REST route cannot
			 * be used because the cart session is not loaded for it.
			 */
			const response = await fetch(pmw.ajax_url, {
				method  : "POST",
				cache   : "no-cache",
				body    : new URLSearchParams({action: "pmw_get_cart_items"}),
				redirect: "error",
			});

			if (!response.ok) throw Error("Error getting cart items from backend");

			const data = await response.json();

			if (!data.success) throw Error("Error getting cart items from backend");

			const serverCart = data.data["cart"] ? data.data["cart"] : {};

			// The product details have to be in the data layer before any event
			// is emitted, or the events would carry no commerce fields.
			pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, serverCart);

			if (data.data["cart_item_keys"]) {
				pmwDataLayer.cart_item_keys = Object.assign(
					pmwDataLayer.cart_item_keys || {},
					data.data["cart_item_keys"]
				);
			}

			const localCart  = pmwDataLayer.cart ? pmwDataLayer.cart : {};
			const productIds = [...new Set([...Object.keys(localCart), ...Object.keys(serverCart)])];

			let emitted = false;

			for (const productId of productIds) {

				const serverQuantity = Number(serverCart?.[productId]?.quantity) || 0;
				const localQuantity  = Number(localCart?.[productId]?.quantity) || 0;

				if (serverQuantity > localQuantity) {
					emitted = true;
					await pmw.addProductToCart(productId, serverQuantity - localQuantity);
				} else if (serverQuantity < localQuantity) {
					emitted = true;
					await pmw.removeProductFromCart(productId, localQuantity - serverQuantity);
				}
			}

			/**
			 * The delta path above already keeps pmwDataLayer.cart in step. When
			 * nothing had to be emitted, adopt the server cart verbatim, so
			 * fields other than the quantity (a price changed by a coupon, for
			 * instance) still catch up.
			 */
			if (!emitted) {
				pmw.saveCartObjectToDataLayer(serverCart);
				pmw.storeCartData(serverCart);
			}

			pmw.cartSyncedFromBackend = true;
			pmw.storeData("cart_hash", pmw.platform.getCartHash());

		} catch (e) {
			pmw.console.error("Pixel Manager: reconciling the cart with the backend failed", e);
		}
	};

	// Get product IDs from the backend
	pmw.getProductsFromBackend = async productIds => {

		if (pmwDataLayer?.products) {
			// If productIds already exists as key in pmwDataLayer.products, remove it from the array
			productIds = productIds.filter(productId => !(productId in pmwDataLayer.products));
		}

		// If no product IDs are in the object, don't try to get anything from the server
		if (!productIds || productIds.length === 0) return;

		// if no page_id is set, don't try to get anything from the server
		if (!pmwDataLayer.general.page_id) return;

		// if productIds is not an array, or it's an empty array, don't try to get anything from the server
		if (!Array.isArray(productIds) || productIds.length === 0) return;

		try {

			let data = {
				action     : "pmw_get_product_ids",  // for the AJAX call
				page_id    : pmwDataLayer.general.page_id,
				page_type  : pmwDataLayer.shop.page_type,
				product_ids: productIds,
			};

			const restResponse = await pmw.tryRestOrAjax(
				{
					url : pmw.root + "pmw/v1/products/",
					init: {
						method : "POST",
						cache  : "no-cache",
						headers: {"Content-Type": "application/json"},
						body   : JSON.stringify(data),
					},
				},
				() => {
					// AJAX fallback — fire-and-forget; product data will be missing but
					// tracking still works on pages that already have it in the data layer.
					fetch(pmw.ajax_url, {
						method: "POST",
						cache : "no-cache",
						body  : new URLSearchParams(data),
					})
						.then(r => r.json())
						.then(responseData => {
							if (responseData.success) {
								pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, responseData.data);
							}
						})
						.catch(e => console.error(e));
				},
			);

			if (restResponse) {
				let responseData = await restResponse.json();

				if (!responseData.success) {
					throw new Error("Error getting products from backend: " + responseData.data);
				}

				pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, responseData.data);
			}

		} catch (e) {
			console.error(e);
		}

		return true;
	};

	pmw.saveCartObjectToDataLayer = cartObject => {

		pmwDataLayer.cart     = cartObject;
		pmwDataLayer.products = Object.assign({}, pmwDataLayer.products, cartObject);
	};

	pmw.triggerViewItemEventPrep = async productId => {

		if (!pmwDataLayer?.products?.[productId]) {
			await pmw.getProductsFromBackend([productId]);
		}

		if (!pmwDataLayer?.products?.[productId]) {
			pmw.triggerViewItemEventNoProduct();
			pmw.console.log(`Product with ID ${productId} not found in the data layer and couldn't be retrieved from the backend. Triggering view-item event without product data.`);
		} else {
			pmw.triggerViewItemEvent(productId);
		}
	};

	pmw.triggerViewItemEvent = productId => {

		let product = pmw.getProductDetailsFormattedForEvent(productId);

		if (!product) return;

		pmw.bus.emit("pmw:view-item", product);
	};

	pmw.triggerViewItemEventNoProduct = () => {
		pmw.bus.emit("pmw:view-item");
	};

	pmw.fireCheckoutOption = (step, checkout_option = null, value = null) => {

		let data = {
			step           : step,
			checkout_option: checkout_option,
			value          : value,
		};

		pmw.bus.emit("pmw:checkout-option", data);
	};

	pmw.fireCheckoutProgress = step => {

		let data = {
			step: step,
		};

		pmw.bus.emit("pmw:checkout-progress", data);
	};

	pmw.getPostIdFromString = string => {

		try {
			return string.match(/(post-)(\d+)/)[2];
		} catch (e) {
			console.error(e);
		}
	};


	pmw.getProductDataForViewItemEvent = productId => {

		if (!productId) throw Error("Wasn't able to retrieve a productId");

		try {
			if (pmwDataLayer.products[productId]) {

				return pmw.getProductDetailsFormattedForEvent(productId);
			}
		} catch (e) {
			console.error(e);
		}
	};


	pmw.getSearchTermFromUrl = () => {

		try {
			let urlParameters = new URLSearchParams(window.location.search);
			return urlParameters.get("s");
		} catch (e) {
			console.error(e);
		}
	};


	/**
	 * Sets a new cookie or updates an existing one with the provided name and value.
	 *
	 * @function setCookie
	 * @global
	 * @param {string} cookieName - The name of the cookie.
	 * @param {string} [cookieValue=""] - The value to set for the cookie. Default value is an empty string.
	 * @param {number|null} [expiryDays=null] - The number of days until the cookie expires. If not provided, the cookie will last until the end of the session.
	 * @returns {void}
	 *
	 * @example
	 * // This will create a cookie "username" with the value "JohnDoe" that expires in 7 days
	 * pmw.setCookie("username", "JohnDoe", 7);
	 */
	pmw.setCookie = (cookieName, cookieValue = "", expiryDays = null) => {

		let cookie = cookieName + "=" + cookieValue;

		if (expiryDays) {

			let d = new Date();
			d.setTime(d.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
			cookie += ";expires=" + d.toUTCString();
		}

		cookie += ";path=/";

		// On HTTPS pages the cookie is marked Secure, as the PHP side already
		// does with is_ssl(). PCI scans flag cookies without it. SameSite stays
		// unset on purpose: an explicit Lax would drop Chrome's Lax+POST grace
		// for cross-site payment returns (DD-059).
		if (window.location.protocol === "https:") {
			cookie += ";Secure";
		}

		document.cookie = cookie;
	};

	/**
	 * Retrieves the value of a specific cookie from the document's cookie string.
	 *
	 * @param {String} cookieName - The name of the cookie to retrieve.
	 * @returns {String} - The value of the cookie if found, an empty string otherwise.
	 *
	 * @example
	 * // returns 'value' if 'cookieName=value' exists in document.cookie
	 * pmw.getCookie('cookieName')
	 */
	/**
	 * Recursively strip null and undefined values from an event payload.
	 *
	 * Some pixel endpoints reject an entire request when a property is
	 * explicitly null (TikTok Events API), and optional chaining fallbacks
	 * can leave undefined values in adapter output (e.g. currency when the
	 * shop object is unavailable). The event processor applies this to every
	 * adapter result so no pixel receives null or undefined parameters.
	 *
	 * @since 1.61.1
	 *
	 * @param {*} value Value to sanitize.
	 * @returns {*} The value with null/undefined entries removed.
	 */
	pmw.stripNullish = value => {

		if (Array.isArray(value)) {
			return value
				.map(item => pmw.stripNullish(item))
				.filter(item => item !== null && item !== undefined);
		}

		if (value && typeof value === "object") {
			const out = {};
			for (const [ key, val ] of Object.entries(value)) {
				if (val === null || val === undefined) continue;
				out[key] = pmw.stripNullish(val);
			}
			return out;
		}

		return value;
	};

	pmw.getCookie = cookieName => {

		let name          = cookieName + "=";
		let decodedCookie = decodeURIComponent(document.cookie);
		let ca            = decodedCookie.split(";");

		for (let i = 0; i < ca.length; i++) {

			let c = ca[i];

			while (c.charAt(0) == " ") {
				c = c.substring(1);
			}

			if (c.indexOf(name) == 0) {
				return c.substring(name.length, c.length);
			}
		}

		return "";
	};

	/**
	 * Find and get the cookie with a name that contains the string.
	 * If one is found, then return the cookie.
	 * If none is found, then return false.
	 *
	 * @param string
	 * @returns {boolean|string}
	 *
	 * @since 1.32.5
	 */
	pmw.getCookieThatContainsRegex = (regex) => {

		let cookies = document.cookie.split(";");

		for (let i = 0; i < cookies.length; i++) {
			let cookie = cookies[i].trim();

			if (regex.test(cookie)) {
				return cookie;
			}
		}

		return false;
	};

	pmw.deleteCookie = cookieName => {
		pmw.setCookie(cookieName, "", -1);
	};

	/**
	 * Store data in the browser
	 *
	 * Try to store the data in the local storage (session or persistent) if available.
	 * If not available, store the data in the cookie.
	 *
	 * Max cookie expiry is 400 days as specified by Google Chrome: https://developer.chrome.com/blog/cookie-max-age-expires/
	 *
	 * @param key
	 * @param value
	 * @param persistent
	 * @param extension - If true, this will be saved in a separate key
	 */
	pmw.storeData = (key, value, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let dataGetter = persistent ? pmw.getPersistentData : pmw.getSessionData;
		let dataSetter = persistent ? pmw.setPersistentData : pmw.setSessionData;

		let data = dataGetter(suffix);
		extension ? data = value : data[key] = value;

		dataSetter(data, suffix);

		// Broadcast session data changes to other tabs
		if (!persistent && pmw.crossTabSync && !pmw.crossTabSync.isSyncing()) {
			pmw.crossTabSync.broadcastUpdate(key, value, extension, suffix);
		}
	};

	pmw.retrieveData = (key, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let data = persistent ? pmw.getPersistentData(suffix) : pmw.getSessionData(suffix);

		return extension ? data : data[key];
	};

	pmw.deleteDataKey = (key, persistent = false, extension = false) => {

		const suffix = extension ? key : "";

		let dataGetter = persistent ? pmw.getPersistentData : pmw.getSessionData;
		let dataSetter = persistent ? pmw.setPersistentData : pmw.setSessionData;

		let data = dataGetter(suffix);
		// extension ? delete data : delete data[key]

		if (extension) {

			try {
				if (persistent && window.localStorage) window.localStorage.removeItem(pmw.getPersistentDataKey(suffix));
				if (!persistent && window.sessionStorage) window.sessionStorage.removeItem(pmw.getSessionDataKey(suffix));

			} catch (e) {
				console.error(e);
			}

			return;
		}

		delete data[key];

		dataSetter(data, suffix);
	};

	pmw.storeCartData = data => {
		pmw.storeData("cart", data, false, true);
	};

	pmw.retrieveCartData = () => {
		return pmw.retrieveData("cart", false, true);
	};

	pmw.getSessionDataKey = (suffix = "") => {
		const defaultStorageKey = "_pmw_session_data";

		if (suffix) {
			return defaultStorageKey + "_" + suffix;
		}

		return defaultStorageKey;
	};

	pmw.getPersistentDataKey = (storageKeySuffix) => {

		const defaultStorageKey = "_pmw_persistent_data";

		if (storageKeySuffix) {
			return defaultStorageKey + "_" + storageKeySuffix;
		}

		return defaultStorageKey;
	};

	pmw.getSessionData = (suffix = "") => {

		if (window.sessionStorage) {

			let data = window.sessionStorage.getItem(pmw.getSessionDataKey(suffix));

			return data !== null ? JSON.parse(data) : {};
		} else {
			// fallback to cookie
			let data = pmw.getCookie(pmw.getSessionDataKey(suffix));

			return data !== "" ? JSON.parse(data) : {};
		}
	};

	pmw.setSessionData = (data, storageKeySuffix = "") => {
		if (window.sessionStorage) {
			window.sessionStorage.setItem(pmw.getSessionDataKey(storageKeySuffix), JSON.stringify(data));
		} else {
			// fallback to cookie
			pmw.setCookie(pmw.getSessionDataKey(storageKeySuffix), JSON.stringify(data));
		}
	};

	pmw.getPersistentData = (storageKeySuffix) => {

		if (window.localStorage) {

			let data = window.localStorage.getItem(pmw.getPersistentDataKey(storageKeySuffix));

			return data !== null ? JSON.parse(data) : {};
		} else {
			// fallback to cookie
			let data = pmw.getCookie(pmw.getPersistentDataKey(storageKeySuffix));

			return data !== "" ? JSON.parse(data) : {};
		}
	};

	pmw.setPersistentData = (data, storageKeySuffix = "") => {

		if (window.localStorage) {
			window.localStorage.setItem(pmw.getPersistentDataKey(storageKeySuffix), JSON.stringify(data));
		} else {
			// fallback to cookie
			pmw.setCookie(pmw.getPersistentDataKey(storageKeySuffix), JSON.stringify(data), 400);
		}
	};

	pmw.storeOrderIdOnServer = async (orderDetails) => {

		try {

			const body = {
				action   : "pmw_purchase_pixels_fired",  // for the AJAX call
				order_id : orderDetails.orderId,
				order_key: orderDetails.orderKey,
				source   : orderDetails.source,
			};

			const restResponse = await pmw.tryRestOrAjax(
				{
					url : pmw.root + "pmw/v1/pixels-fired/",
					init: {
						method   : "POST",
						headers  : {"Content-Type": "application/json"},
						body     : JSON.stringify(body),
						keepalive: true,
						cache    : "no-cache",
						redirect : "error",
					},
				},
				() => {
					pmw.console.log("REST API not available, falling back to AJAX");
					fetch(pmw.ajax_url, {
						method   : "POST",
						body     : new URLSearchParams(body),
						keepalive: true,
						redirect : "error",
					})
						.then(r => r.json())
						.then(responseJson => {
							responseJson.success
								? pmw.console.log("", responseJson.data)
								: pmw.console.error("", responseJson.data);
						})
						.catch(e => console.error(e));
				},
			);

			if (restResponse) {
				const responseJson = await restResponse.json();

				if (responseJson.success) {
					pmw.console.log("", responseJson.data);
				} else {
					pmw.console.error("", responseJson.data);
				}
			}

		} catch (e) {
			console.error(e);
		}
	};


	pmw.getProductDetailsFormattedForEvent = (productId, quantity = 1) => {

		// An entry that exists but is empty (a deleted product) is truthy and used
		// to slip past this guard, producing a product of undefined fields.
		if (!pmw.getDataLayerProduct(productId)) return null;

		let product = {
			id           : productId.toString(),
			dyn_r_ids    : pmwDataLayer.products[productId].dyn_r_ids,
			name         : pmwDataLayer.products[productId].name,
			list_name    : pmwDataLayer.shop.list_name,
			brand        : pmwDataLayer.products[productId].brand,
			category     : pmwDataLayer.products[productId].category,
			variant      : pmwDataLayer.products[productId].variant,
			list_position: pmwDataLayer.products[productId].position,
			quantity     : quantity,
			price        : pmwDataLayer.products[productId].price,
			currency     : pmwDataLayer?.shop?.currency,
			is_variable  : pmwDataLayer.products[productId].is_variable,
			is_variation : pmwDataLayer.products[productId].is_variation,
			parent_id    : pmwDataLayer.products[productId].parent_id,
		};

		if (product.is_variation) product["parent_id_dyn_r_ids"] = pmwDataLayer.products[productId].parent_id_dyn_r_ids;

		return product;
	};

	/**
	 * Resolve the currency for a product-level event.
	 *
	 * pmw.getProductDetailsFormattedForEvent() stamps the shop currency onto every
	 * product it builds, so all of PMW's own event paths carry it. Third-party
	 * integrations and site snippets, however, may trigger the public product events
	 * (pmw:add-to-cart, pmw:view-item, pmw:add-to-wishlist, ...) with a raw
	 * pmwDataLayer.products[id] entry, which has no currency key. Without a fallback
	 * the adapters then emit a value with no currency, and pixels like Meta reject
	 * the amount ("Invalid parameter format for currency").
	 *
	 * @since 1.64.1
	 *
	 * @param {Object} product Product object from the event data.
	 * @returns {String|undefined} The product currency, or the shop currency as fallback.
	 */
	pmw.getProductCurrency = product => product?.currency || pmwDataLayer?.shop?.currency;

	pmw.getClidFromBrowser = (clidId = "gclid") => {

		let clidCookieId;

		clidCookieId = {
			gclid: "_gcl_aw",
			dclid: "_gcl_dc",
		};

		if (pmw.getCookie(clidCookieId[clidId])) {

			let clidCookie = pmw.getCookie(clidCookieId[clidId]);
			let matches    = clidCookie.match(/(GCL.[\d]*.)(.*)/);
			return matches[2];
		} else {
			return "";
		}
	};

	pmw.getUserAgent = () => navigator.userAgent;

	pmw.getViewPort = () => ({
		width : Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
		height: Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
	});


	pmw.consoleLogVersion = () => {
		console.log(pmwDataLayer.version);
	};

	/**
	 * Loads a script and caches it using native browser caching.
	 *
	 * Uses script tag injection which:
	 * - Leverages browser's HTTP cache (respects Cache-Control headers)
	 * - Avoids CORS preflight issues (no custom headers sent)
	 * - Auto-executes the script on load
	 * - Prevents duplicate script loading
	 *
	 * When the URL fails, the fallback URLs are tried one after another, in
	 * the given order. A single fallback may still be passed as a string.
	 *
	 * This code is .thenable and resolves with the URL that actually loaded,
	 * so the caller can tell whether a fallback was needed.
	 */

	const loadedScripts = new Set();

	// Script URLs whose load event actually fired. Consumed by the first-run
	// "pixels are live" confirmation to distinguish a truly loaded vendor
	// script (gtag.js) from a merely injected snippet.
	pmw.getLoadedScriptUrls = () => Array.from(loadedScripts);

	pmw.loadScriptAndCacheIt = (url, fallbackUrls) => {

		// Return resolved promise if script already loaded
		if (loadedScripts.has(url)) {
			pmw.console.log("Script already loaded: " + url);
			return Promise.resolve(url);
		}

		const loadScript = (scriptUrl) => {
			return new Promise((resolve, reject) => {

				// Check if script tag already exists in DOM
				if (document.querySelector(`script[src="${scriptUrl}"]`)) {
					loadedScripts.add(scriptUrl);
					pmw.console.log("Script tag already exists: " + scriptUrl);
					resolve(scriptUrl);
					return;
				}

				const script       = document.createElement("script");
				script.src         = scriptUrl;
				script.async       = true;
				script.crossOrigin = "anonymous"; // Enables CORS without sending credentials, avoids preflight issues

				script.onload = () => {
					loadedScripts.add(scriptUrl);
					pmw.console.log("Successfully loaded script: " + scriptUrl);
					resolve(scriptUrl);
				};

				script.onerror = () => {
					script.remove(); // Clean up failed script tag
					reject(new Error("Script failed to load: " + scriptUrl));
				};

				document.head.appendChild(script);
			});
		};

		const fallbacks = (Array.isArray(fallbackUrls) ? fallbackUrls : [fallbackUrls])
			.filter(fallbackUrl => fallbackUrl && fallbackUrl !== url);

		return fallbacks
			.reduce(
				(attempt, fallbackUrl) => attempt.catch(() => {
					pmw.console.log("Script failed, loading fallback: " + fallbackUrl);
					return loadScript(fallbackUrl);
				}),
				loadScript(url),
			)
			.catch(() => {
				if (!fallbacks.length) {
					throw new Error("Primary script failed to load, and no fallback URL provided.");
				}
				throw new Error("The primary script and every fallback script failed to load.");
			});
	};

	pmw.getOrderItemPrice = orderItem => (orderItem.total + orderItem.total_tax) / orderItem.quantity;

	pmw.hasLoginEventFired = () => {
		let data = pmw.getSessionData();
		return data?.loginEventFired;
	};

	pmw.setLoginEventFired = () => {
		let data                = pmw.getSessionData();
		data["loginEventFired"] = true;
		pmw.setSessionData(data);
	};

	pmw.hasAccountCreatedEventFired = () => {
		let data = pmw.getSessionData();
		return data?.accountCreatedEventFired;
	};

	pmw.setAccountCreatedEventFired = () => {
		let data                         = pmw.getSessionData();
		data["accountCreatedEventFired"] = true;
		pmw.setSessionData(data);
	};

	pmw.pageLoaded = async () => new Promise(resolve => {
		(function waitForVar() {
			if ("complete" === document.readyState) return resolve();
			setTimeout(waitForVar, 50);
		})();
	});

	pmw.pageReady = () => {
		return new Promise(resolve => {
			(function waitForVar() {
				if ("interactive" === document.readyState || "complete" === document.readyState) return resolve();
				setTimeout(waitForVar, 50);
			})();
		});
	};

	pmw.isMiniCartActive = () => {
		if (window.sessionStorage) {
			Object.keys(window.sessionStorage).forEach(key => {
				if (key.includes("wc_fragments")) {
					return true;
				}
			});
		}

		return false;
	};


	pmw.urlHasParameter = parameter => {
		let urlParams = new URLSearchParams(window.location.search);
		return urlParams.has(parameter);
	};

	pmw.getUrlParameter = parameter => {
		let urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(parameter);
	};

	// https://stackoverflow.com/a/60606893/4688612
	pmw.hashAsync = (algo, str) => {
		return crypto.subtle.digest(algo, new TextEncoder("utf-8").encode(str)).then(buf => {
			return Array.prototype.map.call(new Uint8Array(buf), x => (("00" + x.toString(16)).slice(-2))).join("");
		});
	};

	pmw.getCartValue = () => {

		let value = 0;

		if (pmwDataLayer?.cart) {

			for (const key in pmwDataLayer.cart) {
				// content_ids.push(pmwDataLayer.products[key].dyn_r_ids[pmwDataLayer.pixels.facebook.dynamic_remarketing.id_type])

				let product = pmwDataLayer.cart[key];

				value += product.quantity * product.price;
			}
		}

		return value;
	};

	/**
	 * Detect if the current URL contains at least one pattern
	 *
	 * @param patterns
	 * @returns {boolean}
	 */
	pmw.doesUrlContainPatterns = patterns => {

		for (const pattern of patterns) {
			if (new RegExp(pattern).test(window.location.href)) {
				return true;
			}
		}

		return false;
	};

	/**
	 * Detect if the current URL contains at least one pattern that is on the tracking exclusion list
	 *
	 * https://www.linkedin.com/pulse/how-remove-google-robot-problem-via-gtm-facebook-pixel-hjelpdahl/
	 * https://www.youtube.com/watch?v=b4I1ePZt8Z0
	 *
	 * @returns {boolean}
	 */
	pmw.excludeDomainFromTracking = () => {

		let exclude_domains = [
			"appspot.com",
			"translate.google.com",
		];

		if (pmwDataLayer?.general?.exclude_domains) {
			exclude_domains = [...exclude_domains, ...pmwDataLayer.general.exclude_domains];
		}

		// Abort if URL contains excluded domains
		if (exclude_domains.some(domain => window.location.href.includes(domain))) {
			console.debug("Pixel Manager: Aborted due to excluded domain");
			return true;
		}

		return false;
	};

	/**
	 * Get a random event ID
	 *
	 * @param length
	 * @return {string}
	 */
	pmw.getRandomEventId = (length = 16) => {
		let id = "";
		while (id.length < length) {
			id += (Math.random() + 1).toString(36).substring(2);
		}
		return "pmw_" + id.substring(0, length);
	};

	pmw.pmwConsoleMessage = () => {
		let message = "Pixel Manager: ";
		message += pmwDataLayer.version.pro ? "pro" : "free";
		message += " | distro: " + pmwDataLayer.version.distro;
		if (pmwDataLayer.version.distro === "fms" && pmwDataLayer.version.pro) {
			message += " | active license: " + (pmwDataLayer.version.eligible_for_updates ? "yes" : "no");
		}
		message += " | version: " + pmwDataLayer.version.number;

		if (pmwDataLayer.version.show === true) {
			console.log(message);
		} else {
			pmw.console.log(message);
		}
	};

	pmw.canLoadPremiumFeatures = () => {
		return (pmwDataLayer.version.distro === "fms" && pmwDataLayer.version.pro && pmwDataLayer.version.eligible_for_updates) || pmwDataLayer.version.distro === "wcm";
	};

	/**
	 * State if the pmw:dom:ready event has been fired
	 *
	 * @type {boolean}
	 */
	let pmwDomReadyFired = false;


	/**
	 * Trigger the pmw:dom:ready event
	 *
	 * @returns {void}
	 */
	pmw.triggerPmwDomReadyEvent = () => {

		// If the event has already been fired, return
		if (pmwDomReadyFired) return;

		// If the document is already loaded, fire the event
		if (document.readyState === "complete") {
			firePmwDomReadyEvent();
			return;
		}

		// Continue processing if the DOM has not yet been loaded.
		// pmw.onDocumentReady runs immediately when the document is already
		// parsed, otherwise on DOMContentLoaded; firePmwDomReadyEvent is
		// idempotent either way.
		pmw.onDocumentReady(() => {
			firePmwDomReadyEvent();
		});
	};

	/**
	 * Fire the pmw:dom:ready event
	 *
	 * @returns {void}
	 */
	const firePmwDomReadyEvent = () => {

		// If the event has already been fired, return
		if (pmwDomReadyFired) return;

		// Fire the event
		pmw.bus.emitAndDispatch("pmw:dom:ready");

		// Set the flag to true to indicate that the event has been fired
		pmwDomReadyFired = true;
	};

	pmw.getEmailFromTarget = target => {

		// Only try to add the clicked email if href is set
		if (target.href) {

			// Get the email from the link
			// But only if there is a valid email address in the link
			// Also, the href may contain other parameters, so we need to check for that and remove them
			let email = target.href.replace("mailto:", "");


			if (email.indexOf("?") > -1) {
				email = email.split("?")[0];
			}

			// Trim the email and remove all whitespaces
			email = email.replace(/\s/g, "");

			// If the email is not empty and valid, add it to the data object
			if (email && pmw.isEmail(email)) {
				return email;
			}
		}

		return "";
	};

	pmw.sendEventPayloadToServer = payload => {
		if (typeof pmw.sendEventPayloadToServerPremium === "function") {
			// Terminal catch: the send is fire-and-forget, so a rejection would
			// otherwise escape as an unhandled promise rejection.
			pmw.sendEventPayloadToServerPremium(payload).catch(e => {
				pmw.console.error("Server-side send failed:", e && e.message ? e.message : e);
			});
		}
	};

	/**
	 * The consent category each pixel's loader is gated on, for the pixels that
	 * can be listed in pageview_event_s2s.pixels
	 * (Options::pixels_that_require_s2s_pageview_events()). There is no shared
	 * pixel to category registry: every loader passes its category to
	 * pmw.consent.canPixelBeFired() in its own event_listeners.js, and these
	 * entries mirror those calls. A pixel missing here is treated as marketing,
	 * the category of every ad platform pixel.
	 *
	 * @since 1.69.3
	 */
	const PAGE_VIEW_PIXEL_CONSENT_CATEGORIES = {
		facebook: "marketing",
		snapchat: "marketing",
		reddit  : "marketing",
		openai  : "marketing",
	};

	/**
	 * Whether the visitor currently grants the consent category a pixel's
	 * loader requires. Reads the categories directly rather than through
	 * pmw.consent.canPixelBeFired(), which logs a suppression line on every
	 * denied call and would do so on every poll.
	 *
	 * @param {string} pixelName
	 * @return {boolean}
	 *
	 * @since 1.69.3
	 */
	const isPageViewPixelConsentGranted = pixelName => {
		const category   = PAGE_VIEW_PIXEL_CONSENT_CATEGORIES[pixelName] || "marketing";
		const categories = pmw.consent?.categories?.get?.();

		// Without a readable consent state, keep the previous behaviour and
		// wait (bounded by maxWait) rather than guess a denial.
		if (!categories) return true;

		return Boolean(categories[category]);
	};

	/**
	 * Waits for the pixels that feed the S2S page view to load, then triggers
	 * the page view event.
	 *
	 * pmw:page-view drives the browser page_view of EVERY pixel, not only the
	 * S2S one, so the wait is only a courtesy to the S2S page view: it gives a
	 * pixel such as Facebook the chance to load and write its browser
	 * identifiers (e.g. _fbp) before the S2S payload is built. It therefore
	 * only runs when the S2S page view is active, and it is bounded: a pixel
	 * that never loads (ad blocker, script error) must not swallow the page
	 * view of all the others. After maxWait the event fires anyway, and it
	 * fires exactly once.
	 *
	 * A pixel whose consent category the visitor has denied is not waited for
	 * at all, because its loader is gated on that category and it will never
	 * load. The check runs on every poll, so a grant that arrives during the
	 * wait puts the pixel back on the list, still bounded by maxWait.
	 *
	 * @param {number} maxWait Upper bound in milliseconds for the wait.
	 * @return {Promise<void>}
	 *
	 * @since 1.49.0
	 * @since 1.69.3 Skips the wait when the S2S page view is inactive, skips pixels whose consent category is denied and stops waiting after maxWait.
	 */
	pmw.waitForPixelsAndTriggerPageView = async (maxWait = 3000) => {

		// Guard against duplicate page view events (e.g. when consent API re-triggers pmw:load-pixels)
		if (pmw._pageViewTriggered) {
			pmw.console.log("Page view event already triggered, skipping duplicate.");
			return Promise.resolve();
		}
		pmw._pageViewTriggered = true;

		return new Promise(resolve => {

			let fired = false;

			const triggerPageView = () => {
				if (fired) return;
				fired = true;
				pmw.bus.emitAndDispatch("pmw:page-view");
				resolve();
			};

			// Check if at least one pixel is active in the data layer
			if (!pmwDataLayer?.pixels) {
				pmw.console.error("pmwDataLayer or pmwDataLayer.pixels is not defined, triggering page view event immediately.");
				triggerPageView();
				return;
			}

			const pageViewS2s = pmwDataLayer?.general?.server_2_server?.pageview_event_s2s;

			// Without an active S2S page view there is nothing to wait for
			if (!pageViewS2s?.is_active) {
				triggerPageView();
				return;
			}

			let activePixels = pageViewS2s.pixels || [];

			// This line filters the activePixels array, keeping only those pixel names that exist as keys
			// in the pmwDataLayer.pixels object (i.e., only pixels that are currently defined/active in the data layer)
			activePixels = activePixels.filter(pixelName => pmwDataLayer.pixels[pixelName]);

			// If no pixels to check, trigger event immediately
			if (activePixels.length === 0) {
				pmw.console.log("No specific pixels to wait for, triggering page view event.");
				triggerPageView();
				return;
			}

			const pendingPixels = () => activePixels.filter(pixelName =>
				pmwDataLayer.pixels[pixelName]
				&& !pmwDataLayer.pixels[pixelName].loaded
				&& isPageViewPixelConsentGranted(pixelName));

			let pollTimer;

			// Stop waiting after maxWait. Timer based rather than Date.now()
			// based, so the bound holds however the clock is read.
			const deadlineTimer = setTimeout(() => {
				clearTimeout(pollTimer);
				pmw.console.log("Pixels not loaded in time, triggering page view event without them:", pendingPixels());
				triggerPageView();
			}, maxWait);

			// Check if all active pixels are loaded
			const checkPixelsLoaded = () => {
				if (pendingPixels().length === 0) {
					clearTimeout(deadlineTimer);
					triggerPageView();
					return;
				}

				pollTimer = setTimeout(checkPixelsLoaded, 100);
			};

			checkPixelsLoaded();
		});
	};


	/**
	 * The pmwDataLayer.products entry for a product id, or null when there is no
	 * usable product data for it.
	 *
	 * get_product_data() renders an EMPTY entry for every product that
	 * wc_get_product() no longer resolves, which is what an order containing a
	 * since-deleted product looks like: the key is there, its contents are not.
	 * Reaching through such an entry throws, and because the adapters catch their
	 * own errors, one deleted product used to drop the entire purchase conversion
	 * for that pixel. Skip any line this returns null for.
	 *
	 * @param {string|number} productId
	 * @returns {object|null}
	 */
	pmw.getDataLayerProduct = productId => {

		const product = pmwDataLayer?.products?.[productId];

		return ( product && product.dyn_r_ids ) ? product : null;
	};


	/**
	 * Retrieves the product ID associated with a specific pixel.
	 *
	 * Accepts either a pmwDataLayer.products entry (which carries dyn_r_ids) or a
	 * raw order/cart line item (which does not). Order items only carry ids, so
	 * for those the dyn_r_ids are resolved from pmwDataLayer.products, preferring
	 * the variation over its parent. Never throws: an unresolvable id is logged
	 * and returned as null so one bad line can't take down the whole event.
	 *
	 * @param {object} product   - A products-map entry or an order/cart line item.
	 * @param {string} pixelName - The pixel to get the product ID for.
	 * @returns {string|null} - The product ID for the given pixel,
	 *                          or null if no product ID could be resolved.
	 */
	pmw.getProductIdForSpecificPixel = (product, pixelName) => {

		const idType = pmwDataLayer?.pixels?.[pixelName]?.dynamic_remarketing?.id_type;

		if (!product || !idType) return null;

		// Order and cart line items carry no dyn_r_ids of their own. Look them up
		// in the products map, which the server fills for every order product and
		// saveCartObjectToDataLayer fills for every cart product.
		let dynRIds = product.dyn_r_ids;

		if (!dynRIds) {
			const productId = product.variation_id || product.id;
			dynRIds         = pmwDataLayer?.products?.[productId]?.dyn_r_ids
				|| pmwDataLayer?.products?.[product.id]?.dyn_r_ids;
		}

		if (!dynRIds || undefined === dynRIds[idType]) {
			pmw.console.warn(
				"Could not resolve the " + pixelName + " product id for " + ( product.name || product.id )
			);
			return null;
		}

		return String(dynRIds[idType]);
	};


	pmw.makeFullUrl = url => {

		// Trim any leading or trailing whitespace
		url = url.trim();

		// Check if the URL already contains a protocol
		if (/^(http:\/\/|https:\/\/)/.test(url)) {
			return url;
		}

		// Remove leading slashes
		url = url.replace(/^\/+/, "");

		// Add 'https://' as default protocol
		return "https://" + url;
	};


	/**
	 * Initializes the command queue for the application to ensure that commands
	 * are queued and executed in a controlled manner. This function sets up
	 * the underlying mechanism to manage and process commands effectively.
	 *
	 * Typically, the command queue is used by external developers
	 * who want to make sure that their Pixel Manager API calls safely get executed,
	 * no matter if the initialization order of the Pixel Manager is shuffled,
	 * delayed, or lazy loaded.
	 *
	 * @since 1.49.0
	 */
	pmw.initializeCommandQueue = () => {

		const queue = window._pmwq = window._pmwq || [];

		/**
		 * First line of a queued function's source.
		 *
		 * A queued command that throws is swallowed so that one broken snippet
		 * cannot take down the rest of the Pixel Manager. That makes the
		 * failure invisible, and everything after the throwing line - usually
		 * the integration's event listener registration - is skipped. Logging
		 * the head of the source identifies which snippet died.
		 *
		 * @since 1.65.0
		 * @param {Function} fn
		 * @returns {string}
		 */
		const describe = (fn) => {
			try {
				return String(fn).replace(/\s+/g, " ").slice(0, 120);
			} catch (e) {
				return "(source unavailable)";
			}
		};

		const run = (fn, label) => {
			if (typeof fn !== "function") return;
			try {
				fn();
			} catch (e) {
				pmw.console.error(`Error executing ${label}. The rest of this command was skipped: ${describe(fn)}`, e);
			}
		};

		queue.forEach(function (fn) {
			run(fn, "queued command");
		});

		// Override push to execute commands immediately now that PMW is ready
		queue.push = function (fn) {
			run(fn, "command from queue");
		};
	};

}(window.pmw = window.pmw || {}));
